import { Injectable } from '@angular/core';
import { select, Store } from '@ngrx/store';
import { map } from 'rxjs/operators';
import { Observable } from 'rxjs';
import { AnalysisResults, AppAccessLevel } from '@sleipai/sleip-models';
import * as AnalysisActions from '../actions/analysis.actions';
import * as AnalysisReducer from '../reducers/analysis.reducer';
import * as AnalysisSelectors from '../selectors/analysis.selectors';
import { Update } from '@ngrx/entity';

@Injectable()
export class AnalysiStoreFacade {
  constructor(private readonly store: Store<AnalysisReducer.State>) {}

  currentAnalysisEntity$ = (recordingId?: string) =>
    this.store.pipe(
      select(AnalysisSelectors.getCurrentAnalysisEntity(recordingId))
    );

  getAnalysisResultsByIds$ = (recordingIds: string[]) => {
    return this.store.pipe(
      select(AnalysisSelectors.selectMultipleAnalysisResults(recordingIds))
    );
  };

  updateSingleAnalysis$ = (update: Update<any>) => {
    return this.store.dispatch(
      AnalysisActions.updateSingleAnalysis({ update })
    );
  };

  fetchAnalysisResultsByIds$ = (recordingIds: string[]) => {
    return this.store.dispatch(
      AnalysisActions.loadMultipleAnalysisResultsByIds({ recordingIds })
    );
  };

  currentAnalysisEntityLoaded$ = (recordingId?: string) =>
    this.store.pipe(
      select(AnalysisSelectors.getCurrentAnalysisEntity(recordingId)),
      map((analysis: Partial<AnalysisResults> | null) => analysis !== null)
    );

  setCurrentAnalysisEntity(analysis: AnalysisResults) {
    return this.store.dispatch(
      AnalysisActions.addSingleAnalysis({ analysis: analysis })
    );
  }

  loadAnalysisById(id: string, isFlexion: boolean) {
    return this.store.dispatch(AnalysisActions.loadAnalysisById({ id, isFlexion }));
  }

  getIsLoading$: Observable<boolean> = this.store.pipe(
    select(AnalysisSelectors.selectIsLoading)
  );

  getIsAnalysisSharing$: Observable<boolean> = this.store.pipe(
    select(AnalysisSelectors.selectIsAnalysisSharing)
  );

  shareAnalysisResult$ = (
    recordingIds: string[],
    horseId: string,
    version: string = 'v2'
  ) => {
    return this.store.dispatch(
      AnalysisActions.shareAnalysis({ recordingIds, horseId, version })
    );
  };

  generateAnalysisResultPDF$ = (
    recordingIds: string[],
    horseId: string,
    userType: AppAccessLevel,
    timeZone: string
  ) => {
    return this.store.dispatch(
      AnalysisActions.generateAnalysisResultPDF({
        recordingIds,
        horseId,
        userType,
        timeZone,
      })
    );
  };

  cancelAnalysisShare(): void {
    this.store.dispatch(AnalysisActions.cancelShareAnalysis());
  }

  getAnalysisResultSharedSuccessLink$: Observable<string | null> =
    this.store.pipe(select(AnalysisSelectors.selectIsAnalysisharedSuccessLink));

  getAnalysisResultPDFGeneratedLink$: Observable<string | null> =
    this.store.pipe(select(AnalysisSelectors.selectIsAnalysisPDFGeneratedLink));

  getAnalysisShareError$: Observable<string | null> = this.store.pipe(
    select(AnalysisSelectors.selectAnalysisShareError)
  );
}
